// NEW_FILE_JUSTIFIED: Review & Publish page (Step 4 of type creation wizard) — distinct from TypeBuilderPage (builder UI) and InsuranceTypesListPage (list view)
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, AlertTriangle, Sparkles, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TypeWizardStepper } from '@/components/insurance/types/Wizard/Stepper';
import { useTypeReviewData } from '@/hooks/useTypeReviewData';
import type { TReviewSectionPreview } from '@/@types/insuranceBuilder';
import { isValidOrgTypeId } from '@/lib/type-guards';
import type { Id } from '@convex/_generated/dataModel';
import { toast } from 'sonner';

export function ReviewPublishPage() {
  const { typeId: rawTypeId } = useParams();
  const typeId = rawTypeId && isValidOrgTypeId(rawTypeId) ? rawTypeId : null;
  if (!typeId) {
    return <div className="p-10 text-sm text-muted-foreground">Invalid type id.</div>;
  }
  return <ReviewPublishContent typeId={typeId} />;
}

function ReviewPublishContent({ typeId }: { typeId: Id<'orgInsuranceTypes'> }) {
  const navigate = useNavigate();
  const { snapshot, publish } = useTypeReviewData(typeId);

  if (!snapshot)
    return (
      <div role="status" aria-live="polite" className="p-10 text-sm text-muted-foreground">
        Loading…
      </div>
    );

  const isPublished = snapshot.status === 'published';

  const onPublish = async () => {
    try {
      await publish({ typeId });
      toast.success('Insurance type published');
      navigate('/insurance/types');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to publish';
      toast.error(msg);
    }
  };

  const onDraft = () => toast.info('Saved as draft');

  const steps: Array<{ key: 'source' | 'extract' | 'builder' | 'review'; label: string }> = [
    { key: 'source', label: 'Source' },
    ...(snapshot.isAiGenerated ? [{ key: 'extract' as const, label: 'Extract' }] : []),
    { key: 'builder', label: 'Builder' },
    { key: 'review', label: 'Review' },
  ];

  return (
    <div className="min-h-full bg-background-secondary">
      <header className="border-b bg-card px-6 py-3">
        <TypeWizardStepper steps={steps} currentIndex={steps.length - 1} />
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6 px-10 py-8 max-w-6xl mx-auto">
        <div className="space-y-6">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/insurance/types/${typeId}`)}
              className="px-2 gap-1"
            >
              <ArrowLeft className="w-4 h-4" aria-hidden="true" />
              <span className="sr-only">Back to builder</span>
            </Button>
            <div>
              <h1 className="text-xl font-semibold text-foreground">Review &amp; publish</h1>
              <p className="text-sm text-muted-foreground">
                You&apos;re about to make this type available across Policies, Drafts, and AI
                agents.
              </p>
            </div>
          </div>

          <Card className="p-5 flex items-center gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold">{snapshot.name}</h2>
                {snapshot.providerName && <Badge variant="outline">{snapshot.providerName}</Badge>}
                {snapshot.isAiGenerated && (
                  <Badge variant="secondary" className="bg-primary/15 text-primary">
                    <Sparkles className="h-3 w-3 mr-1" aria-hidden="true" />
                    AI generated
                  </Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {snapshot.sectionCount} sections · {snapshot.fieldCount} fields ·{' '}
                {snapshot.askViaAiCount} lead-intake fields
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/insurance/types/${typeId}`)}
            >
              <Pencil className="h-4 w-4 mr-1" aria-hidden="true" />
              Edit
            </Button>
          </Card>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatTile label="Sections" value={snapshot.sectionCount} />
            <StatTile label="Fields" value={snapshot.fieldCount} />
            <StatTile label="AI intake" value={snapshot.askViaAiCount} />
            <StatTile label="Will apply to" value={`${snapshot.policyCount} policies`} />
          </div>

          {snapshot.policyCount > 0 && (
            <div
              role="status"
              className="flex items-start gap-2 rounded-lg border border-warning/40 bg-warning/10 p-3 text-sm"
            >
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" aria-hidden="true" />
              <p>
                Existing {snapshot.policyCount} policies will keep their original schema. Removed
                fields stay archived on those policies.
              </p>
            </div>
          )}

          <Card className="p-5 space-y-3">
            <h3 className="text-sm font-semibold">Section preview</h3>
            <ul className="space-y-3">
              {snapshot.sectionsPreview.map((s: TReviewSectionPreview) => (
                <li key={s.sectionKey} className="rounded-lg border bg-card p-3">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{s.title}</span>
                    <span className="text-xs text-muted-foreground">
                      {s.fieldCount} fields · {s.columns}-column
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {s.fieldLabels.join(', ')}
                    {s.fieldCount > s.fieldLabels.length && '…'}
                  </p>
                </li>
              ))}
            </ul>
          </Card>
        </div>

        <aside className="space-y-4">
          <Card className="p-5 space-y-3 border-primary/40 bg-primary/5">
            <h2 className="text-sm font-semibold">Ready to publish</h2>
            <ChecklistItem ok={snapshot.readiness.allFieldsLabeled} label="All fields labeled" />
            <ChecklistItem
              ok={snapshot.readiness.atLeastOneRequired}
              label="At least one required field"
            />
            <ChecklistItem ok={snapshot.readiness.atLeastOneAskAi} label="Lead-intake reviewed" />
            <Button className="w-full" onClick={onPublish} disabled={isPublished}>
              {isPublished ? 'Published' : 'Publish insurance type'}
            </Button>
            <Button variant="ghost" className="w-full" onClick={onDraft}>
              Save as draft
            </Button>
          </Card>
        </aside>
      </div>
    </div>
  );
}

function StatTile({ label, value }: { label: string; value: number | string }) {
  return (
    <Card className="p-4">
      <dl>
        <dt className="text-xs text-muted-foreground uppercase">{label}</dt>
        <dd className="text-2xl font-semibold mt-1">{value}</dd>
      </dl>
    </Card>
  );
}

function ChecklistItem({ ok, label }: { ok: boolean; label: string }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <CheckCircle2
        className={ok ? 'h-4 w-4 text-primary' : 'h-4 w-4 text-muted-foreground'}
        aria-hidden="true"
      />
      <span className={ok ? 'text-foreground' : 'text-muted-foreground'}>{label}</span>
    </div>
  );
}
